// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import { initializeApp, getApps } from "firebase/app";
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  onIdTokenChanged,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
} from "firebase/auth";

// Add initialization error state
const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children, onError }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  // Custom claims decoded from the current ID token (e.g. { admin: true }).
  // Kept in sync by onIdTokenChanged, so any token rotation — hourly SDK
  // refresh, login, or the apiClient 403-retry's forced mint — updates it.
  const [claims, setClaims] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [initError, setInitError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // Initialize Firebase with error handling
  useEffect(() => {
    try {
      const firebaseConfig = {
        apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "placeholder-key",
        authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "placeholder-domain",
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "placeholder-project",
        storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "placeholder-bucket",
        appId: process.env.REACT_APP_FIREBASE_APP_ID || "placeholder-app-id",
      };

      // Check for missing critical config
      if (!process.env.REACT_APP_FIREBASE_API_KEY) {
        console.warn("Firebase API key is missing - auth will not work properly");
      }

      // Initialize Firebase once
      const firebaseApp = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
      const auth = getAuth(firebaseApp);

      // Track the ID token, not just the auth state: onIdTokenChanged fires
      // on login/logout AND every time a new token is minted (hourly SDK
      // rotation, or a forced refresh after a 403 in apiClient), so token +
      // claims state never go stale mid-session. Do NOT force-refresh in
      // here — minting a token re-fires this listener and would loop.
      const unsubscribe = onIdTokenChanged(auth,
        async (user) => {
          setUser(user);
          if (user) {
            try {
              const result = await user.getIdTokenResult();
              setToken(result.token);
              setClaims(result.claims);
            } catch (err) {
              console.error("Failed to get ID token:", err);
              setToken(null);
              setClaims(null);
            }
          } else {
            setToken(null);
            setClaims(null);
          }
          setLoading(false);
          setInitialized(true);
        },
        (error) => {
          console.error("Auth state change error:", error);
          setLoading(false);
          setInitialized(true);
        }
      );

      return unsubscribe;
    } catch (error) {
      console.error("Firebase initialization error:", error);
      setInitError(error);
      setLoading(false);
      setInitialized(true);
      if (onError) onError(error);
      
      // Return a no-op function as cleanup
      return () => {};
    }
  }, [onError]);

  // The rest of your methods with error handling
  const handleAuthError = (err) => {
    console.error("Authentication error:", err);
    setAuthError(err.message || "Unknown authentication error");
  };
  const logout = async () => {
    try {
      await signOut(getAuth());
      setUser(null);
      setToken(null);
      setClaims(null);
    } catch (err) {
      handleAuthError(err);
    }
  };

  // Force-mint a fresh ID token so server-side permission changes (custom
  // claims) take effect immediately. The mint fires onIdTokenChanged, which
  // updates token + claims state; the fresh claims are also returned for
  // callers that need them synchronously.
  const refreshClaims = async () => {
    const current = getAuth().currentUser;
    if (!current) return null;
    try {
      const result = await current.getIdTokenResult(true);
      return result.claims;
    } catch (err) {
      handleAuthError(err);
      return null;
    }
  };
  
  const register = async (email, password) => {
    if (initError) {
      setAuthError("Authentication service is unavailable");
      return null;
    }
  
    setAuthError(null);
    try {
      const auth = getAuth();
      const userCred = await createUserWithEmailAndPassword(auth, email, password);
      const idToken = await userCred.user.getIdToken();
      setToken(idToken);
      setUser(userCred.user);
      return userCred.user;
    } catch (err) {
      handleAuthError(err);
      return null;
    }
  };
  
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(getAuth(), email);
    } catch (err) {
      handleAuthError(err);
    }
  };
  
  const login = async (email, password) => {
    if (initError) {
      setAuthError("Authentication service is unavailable");
      return null;
    }

    setAuthError(null);
    try {
      const auth = getAuth();
      const userCred = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await userCred.user.getIdToken();
      setToken(idToken);
      setUser(userCred.user);
      return userCred.user;
    } catch (err) {
      handleAuthError(err);
      return null;
    }
  };

  // Shared popup OAuth flow used by Google + Apple sign-in. Firebase
  // decides between sign-in vs new-account-creation based on the email's
  // existence in the project.
  const loginWithProvider = async (provider) => {
    if (initError) {
      setAuthError("Authentication service is unavailable");
      return null;
    }

    setAuthError(null);
    try {
      const auth = getAuth();
      const userCred = await signInWithPopup(auth, provider);
      const idToken = await userCred.user.getIdToken();
      setToken(idToken);
      setUser(userCred.user);
      return userCred.user;
    } catch (err) {
      // Popup-closed-by-user is a normal cancel, not an error to display
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        return null;
      }
      handleAuthError(err);
      return null;
    }
  };

  const loginWithGoogle = () => loginWithProvider(new GoogleAuthProvider());

  const loginWithApple = () => {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    return loginWithProvider(provider);
  };

  // Update other methods similarly with initError checks
  // ... (logout, register, resetPassword remain mostly the same)

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        claims,
        isAdmin: claims?.admin === true,
        refreshClaims,
        login,
        loginWithGoogle,
        loginWithApple,
        register,
        logout,
        resetPassword,
        authError,
        loading,
        initError,
        initialized
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
